/**
 * Generate a UUID to use as anonymous identifier
 */
export const generateAnonymousId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Get the current anonymous ID from storage
 */
export const getAnonymousId = (): string | null => {
  try {
    return localStorage.getItem('anonymousId');
  } catch (error) {
    console.warn('Unable to access localStorage:', error);
    return null;
  }
};

/**
 * Set the anonymous ID in storage
 */
const setAnonymousId = (anonymousId: string): void => {
  try {
    localStorage.setItem('anonymousId', anonymousId);
    // Set as a cookie for server-side access
    try {
      document.cookie = `anonymous_id=${anonymousId}; path=/; max-age=31536000; SameSite=Lax`;
    } catch (error) {
      console.warn('Unable to set cookie:', error);
    }
  } catch (error) {
    console.warn('Unable to access localStorage:', error);
  }
};

/**
 * Initialize analytics by setting up the anonymous ID
 */
export const initializeAnalytics = async (): Promise<string> => {
  let anonymousId = getAnonymousId();

  // If no anonymous ID exists, create and store one
  if (!anonymousId) {
    try {
      anonymousId = generateAnonymousId();
      setAnonymousId(anonymousId);
    } catch (error) {
      console.error('Failed to initialize analytics:', error);
      // Return a temporary ID that won't be persisted
      return generateAnonymousId();
    }
  }

  return anonymousId;
};

/**
 * Extract UTM parameters from URL
 */
export const extractUtmParameters = (): Record<string, string> => {
  const params: Record<string, string> = {};
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.has('utm_source')) {
    params.source = urlParams.get('utm_source')!;
  }
  if (urlParams.has('utm_medium')) {
    params.medium = urlParams.get('utm_medium')!;
  }
  if (urlParams.has('utm_campaign')) {
    params.campaign = urlParams.get('utm_campaign')!;
  }
  if (urlParams.has('utm_term')) {
    params.term = urlParams.get('utm_term')!;
  }
  if (urlParams.has('utm_content')) {
    params.content = urlParams.get('utm_content')!;
  }

  return params;
};

// Export a default object with all methods
const analytics = {
  initializeAnalytics,
  extractUtmParameters,
  generateAnonymousId,
};

export default analytics;
